/**
 * GuidesPage - Admin-only page for managing guides
 *
 * Features:
 * - List all guides with GuidesListView
 * - Create/Edit guides with GuideFormDialog
 * - Admin-only access (protected by route)
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import { GuidesListView, GuideFormDialog } from '@modules/guides';
import { GuideService } from '@modules/firebase-core/services/GuideService';

export default function GuidesPage() {
  const { isAdmin, currentUser } = useAuth();

  // GuideService instance (guides are global, not project-specific)
  const guideService = useMemo(() => new GuideService(), []);

  // Guide form dialog state
  const [guideFormDialogOpen, setGuideFormDialogOpen] = useState(false);
  const [guideFormMode, setGuideFormMode] = useState('create');
  const [selectedGuideForEdit, setSelectedGuideForEdit] = useState(null);
  const guideCreateButtonRef = useRef(null);

  // Handler to open create dialog
  const handleOpenCreateGuideDialog = useCallback(() => {
    console.log('[GuidesPage] Opening GuideFormDialog in CREATE mode');
    setGuideFormMode('create');
    setSelectedGuideForEdit(null);
    setGuideFormDialogOpen(true);
  }, []);

  // Handler to open edit dialog
  const handleOpenEditGuideDialog = useCallback((guide) => {
    console.log('[GuidesPage] Opening GuideFormDialog in EDIT mode:', guide.id);
    setGuideFormMode('edit');
    setSelectedGuideForEdit(guide);
    setGuideFormDialogOpen(true);
  }, []);

  // Handler when guide is saved
  const handleGuideFormSave = useCallback((savedGuide) => {
    console.log('[GuidesPage] Guide saved:', savedGuide.id);
    console.log('[GuidesPage] Mode was:', guideFormMode);
    // Real-time listener will update the list automatically
    setGuideFormDialogOpen(false);
  }, [guideFormMode]);

  // Handler to close dialog
  const handleGuideFormClose = useCallback(() => {
    console.log('[GuidesPage] Guide form dialog closed');
    setGuideFormDialogOpen(false);
  }, []);

  // Admin-only check
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please log in to access guides.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Guides Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage application guides and documentation for users.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, mt: 2 }}>
        <GuidesListView
          projectId="" // Guides are not project-specific (global collection)
          guideService={guideService}
          onCreate={handleOpenCreateGuideDialog}
          onEdit={handleOpenEditGuideDialog}
        />
      </Paper>

      {/* Guide Form Dialog */}
      <GuideFormDialog
        open={guideFormDialogOpen}
        mode={guideFormMode}
        projectId="" // Guides are global
        guideId={selectedGuideForEdit?.id}
        initialData={guideFormMode === 'edit' ? selectedGuideForEdit ?? undefined : undefined}
        guideService={guideService}
        onClose={handleGuideFormClose}
        onSave={handleGuideFormSave}
        triggerRef={guideCreateButtonRef}
      />
    </Box>
  );
}
